import { Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { FunctionComponent, ReactNode, useContext, useRef } from 'react';
import { DialogSaveButtons } from '../miscellaneous-components/dialog-save-buttons';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';

export const DialogWrapper: FunctionComponent<{
  children: ReactNode,
  open: boolean,
  onClose: () => void, title: string,
  cancelHandler: () => void,
  submitHandler: () => void,
  isSubmitting: boolean,
  disableSaveButton?: boolean,
  hideCancelButton?: boolean,
  disableCancelButton?: boolean,
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false,
  submitButtonText?: string,
  submitButtonLoadingText?: string,
  /**
   * When true, every dismissal (backdrop click, Escape, the Cancel button) asks
   * "Discard your changes?" before discarding, instead of silently destroying the form.
   * Pass the form's dirty flag (formik.dirty, or useDirtyBaseline's isDirty for
   * programmatically seeded forms). Backdrop/Escape then call onClose; Cancel calls
   * cancelHandler, as before.
   */
  isDirty?: boolean,
}> = ({ children, open, onClose, title, cancelHandler, submitHandler, isSubmitting, disableSaveButton, hideCancelButton, disableCancelButton, maxWidth = 'xs', submitButtonText, submitButtonLoadingText, isDirty }) => {
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isConfirmingDiscard = useRef(false);

  /** True when it is safe to discard: the form is clean, or the user confirmed. */
  const confirmDiscardIfDirty = async (): Promise<boolean> => {
    if (!isDirty) return true;
    if (typeof userNotificationsContext.openDialog !== 'function') {
      console.warn('DialogWrapper: isDirty is set but no UserNotificationsProvider is mounted; closing without confirmation.');
      return true;
    }
    if (isConfirmingDiscard.current) return false;
    isConfirmingDiscard.current = true;
    let confirmed: unknown;
    try {
      // Every inheritable flag is passed explicitly: openDialog merges over the previous
      // dialog's details, so an omitted flag would inherit a stale value (e.g. an
      // Ok-only hideCancelButton from an earlier notice).
      confirmed = await userNotificationsContext.openDialog({
        active: true,
        title: 'Discard your changes?',
        message: 'This form has unsaved changes. Closing it now will discard them.',
        buttonCopy: 'Discard',
        cancelButtonCopy: 'Keep editing',
        hideCancelButton: false,
        have_form_input: false,
        form_value: '',
      });
    } finally {
      isConfirmingDiscard.current = false;
    }
    return confirmed === true;
  };

  // MUI only calls a Dialog's onClose for backdropClick and escapeKeyDown, so everything
  // arriving here is a dismissal rather than an explicit button press.
  const dismissHandler = async () => {
    if (await confirmDiscardIfDirty()) onClose();
  };

  // The Cancel button gets the same guard: a dirty form asks before discarding no matter
  // which exit the user picks. With no isDirty this is a straight cancelHandler call.
  const cancelButtonHandler = async () => {
    if (await confirmDiscardIfDirty()) cancelHandler();
  };

  return (
    <Dialog open={open} onClose={dismissHandler} maxWidth={maxWidth} fullWidth>
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 64px)', overflow: 'hidden' }}>
        <DialogTitle sx={{ pt: 0, pl: 0, pr: 0, flexShrink: 0 }}>
          {title}
        </DialogTitle>
        <DialogContent sx={{ pt: 0, pl: 0, pr: 0, flex: '1 1 auto', overflowY: 'auto' }}>
          {children}
        </DialogContent>
        <DialogActions sx={{ p: 0, flexShrink: 0 }}>
          <DialogSaveButtons
            cancelHandler={cancelButtonHandler}
            submitHandler={submitHandler}
            isSubmitting={isSubmitting}
            disableSaveButton={disableSaveButton}
            hideCancelButton={hideCancelButton}
            disableCancelButton={disableCancelButton}
            buttonInitialText={submitButtonText}
            buttonLoadingText={submitButtonLoadingText}
          />
        </DialogActions>
      </Box>
    </Dialog>
  )
}
